import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getIcon, LEVEL_COLORS } from "@/lib/agents";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Search, Users } from "lucide-react";

export default function Departments() {
  const [depts, setDepts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data.departments));
  }, []);

  const toggle = (id) => setOpen({ ...open, [id]: !open[id] });

  const totalSpec = depts.reduce((s, d) => s + d.specialists_count, 0);
  const filtered = q
    ? depts
        .map((d) => ({
          ...d,
          specialists: d.specialists.filter((s) =>
            `${s.name} ${s.mission}`.toLowerCase().includes(q.toLowerCase())
          ),
        }))
        .filter(
          (d) =>
            d.specialists.length > 0 ||
            `${d.master.name} ${d.master.department}`.toLowerCase().includes(q.toLowerCase())
        )
    : depts;

  return (
    <div className="p-8 space-y-6" data-testid="departments-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">
            {depts.length} DEPARTAMENTOS · {totalSpec} ESPECIALISTAS
          </div>
          <h1 className="font-display font-black text-4xl tracking-tight">Organigrama</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Cada master coordina su equipo. Haz clic en un master para su consola, o en un especialista para chatear.
          </p>
        </div>
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-dim)]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar especialista o departamento…"
            className="rounded-none pl-9 bg-[var(--surface)] border-[var(--border)] font-mono text-sm"
            data-testid="dept-search"
          />
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((d) => {
          const Icon = getIcon(d.master.icon);
          const isOpen = open[d.master.id] ?? true;
          return (
            <div key={d.master.id} className="border border-[var(--border)] bg-[var(--surface)]" data-testid={`dept-${d.master.id}`}>
              <div className="flex items-center gap-3 p-4 border-b border-[var(--border)]">
                <button
                  onClick={() => toggle(d.master.id)}
                  className="text-[var(--text-dim)] hover:text-white"
                  data-testid={`dept-toggle-${d.master.id}`}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <div
                  className="w-9 h-9 border flex items-center justify-center shrink-0"
                  style={{ borderColor: d.master.color, color: d.master.color }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <button
                  onClick={() => nav(`/agents/${d.master.id}`)}
                  className="flex-1 text-left hover:text-[var(--accent)]"
                  data-testid={`dept-master-${d.master.id}`}
                >
                  <div className="label-mono">{d.master.department}</div>
                  <div className="font-display font-bold text-base">{d.master.name}</div>
                </button>
                <Badge className="rounded-none bg-[var(--surface-2)] border border-[var(--border)] text-white hover:bg-[var(--surface-2)] font-mono text-[10px]">
                  <Users className="w-3 h-3 mr-1" /> {d.specialists.length} specs
                </Badge>
                <Badge
                  className="rounded-none font-mono text-[10px]"
                  style={{ background: LEVEL_COLORS[d.master.autonomy_level], color: "#0B0E14" }}
                >
                  L{d.master.autonomy_level}
                </Badge>
              </div>

              {isOpen && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 p-3">
                  {d.specialists.length === 0 && (
                    <div className="col-span-full text-xs text-[var(--text-dim)] font-mono py-4 text-center">
                      Sin especialistas para este filtro.
                    </div>
                  )}
                  {d.specialists.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => nav(`/specialists/${s.id}`)}
                      className="text-left border border-[var(--border)] hover-neon p-3 group"
                      data-testid={`specialist-${s.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono text-[10px] text-[var(--accent)] uppercase truncate">
                          {s.id}
                        </span>
                        <Badge
                          className="rounded-none font-mono text-[9px]"
                          style={{ background: LEVEL_COLORS[s.autonomy_level], color: "#0B0E14" }}
                        >
                          L{s.autonomy_level}
                        </Badge>
                      </div>
                      <div className="text-sm font-semibold text-white mb-1 leading-tight group-hover:text-[var(--accent)]">
                        {s.name}
                      </div>
                      <div className="text-[11px] text-[var(--text-dim)] leading-snug line-clamp-2">
                        {s.mission}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
